import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Run } from "@/api/types";

interface NoiseProgressEvent {
  type: "noise_progress";
  binding_id: string;
  template_id: string;
  sent: number;
  failed: number;
}

export interface NoiseProgressMap {
  [bindingId: string]: { sent: number; failed: number };
}

export function useRunStream(runId: string | undefined, onNoiseProgress: (m: NoiseProgressMap) => void) {
  const qc = useQueryClient();
  const noiseMapRef = useRef<NoiseProgressMap>({});

  useEffect(() => {
    if (!runId) return;
    let es: EventSource | null = null;
    let cancelled = false;
    let backoff = 1000;

    function open() {
      if (cancelled) return;
      es = new EventSource(`/api/v1/runs/${runId}/stream`, { withCredentials: true });
      es.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data && data.type === "noise_progress") {
            const e = data as NoiseProgressEvent;
            noiseMapRef.current = {
              ...noiseMapRef.current,
              [e.binding_id]: { sent: e.sent, failed: e.failed },
            };
            onNoiseProgress(noiseMapRef.current);
            return;
          }
          // Otherwise treat as a Run JSON
          qc.setQueryData(["run", runId], data as Run);
          backoff = 1000; // reset on successful event
        } catch {
          // ignore malformed
        }
      };
      es.onerror = () => {
        es?.close();
        if (cancelled) return;
        setTimeout(open, backoff);
        backoff = Math.min(backoff * 2, 30_000);
      };
    }

    open();
    return () => {
      cancelled = true;
      es?.close();
    };
  }, [runId, qc, onNoiseProgress]);
}
